import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, Layers } from "lucide-react";

/**
 * Shared "Send to pipeline" picker.
 * Props:
 *   open              — controlled
 *   onOpenChange
 *   leadId            — the lead to move (required)
 *   currentBoardId    — highlight where the lead lives now (optional)
 *   currentColumnKey  — same (optional)
 *   onMoved           — callback(newBoardId, newColumnKey)
 */
const COLOR_DOT = {
  neutral: "bg-neutral-400",
  blue: "bg-blue-500",     sky: "bg-sky-500",
  amber: "bg-amber-500",   orange: "bg-orange-500",
  green: "bg-green-500",   emerald: "bg-emerald-500",
  red: "bg-red-500",       rose: "bg-rose-500",
  violet: "bg-violet-500", indigo: "bg-indigo-500",
};

export default function SendToPipelineDialog({
  open, onOpenChange, leadId, currentBoardId, currentColumnKey, onMoved,
}) {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeBoardId, setActiveBoardId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/boards")
      .then(({ data }) => {
        setBoards(data.boards || []);
        setActiveBoardId(currentBoardId || (data.boards?.[0]?.id ?? null));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load pipelines"))
      .finally(() => setLoading(false));
  }, [open, currentBoardId]);

  const activeBoard = boards.find((b) => b.id === activeBoardId);

  const move = async (columnKey) => {
    if (!leadId) return;
    setBusy(true);
    try {
      await api.post(`/boards/${activeBoardId}/move-lead`, {
        lead_id: leadId, column_key: columnKey,
      });
      toast.success(
        `Sent to ${activeBoard?.name} → ${
          activeBoard?.columns?.find((c) => c.key === columnKey)?.label || columnKey
        }`,
      );
      onMoved?.(activeBoardId, columnKey);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="send-pipeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers size={16} /> Send to pipeline
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pick a pipeline and drop this lead straight into the right column —
            e.g. Wholesale → New Contract right after the first call.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" /> Loading pipelines…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Board tabs */}
            <div className="flex gap-1 flex-wrap">
              {boards.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setActiveBoardId(b.id)}
                  data-testid={`stp-board-${b.id}`}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    b.id === activeBoardId
                      ? "bg-[var(--app-black)] text-white border-[var(--app-black)]"
                      : "border-[var(--app-line)] hover:bg-[var(--app-surface)]"
                  }`}
                >
                  {b.name}
                  {b.id === currentBoardId && (
                    <span className="ml-1 text-[9px] uppercase opacity-70">current</span>
                  )}
                </button>
              ))}
            </div>

            {/* Columns for the active board */}
            <div className="grid grid-cols-1 gap-1.5">
              {activeBoard?.columns?.map((c) => {
                const isCurrent = activeBoardId === currentBoardId && c.key === currentColumnKey;
                return (
                  <button
                    key={c.key}
                    onClick={() => !isCurrent && move(c.key)}
                    disabled={busy || isCurrent}
                    data-testid={`stp-col-${c.key}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-left transition-colors ${
                      isCurrent
                        ? "border-neutral-200 bg-neutral-50 opacity-60 cursor-not-allowed"
                        : "border-[var(--app-line)] hover:border-[var(--app-black)] hover:bg-[var(--app-surface)]"
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${COLOR_DOT[c.color] || COLOR_DOT.neutral}`} />
                    <span className="flex-1 text-sm">{c.label}</span>
                    {isCurrent ? (
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Here</span>
                    ) : (
                      <ArrowRight size={12} className="text-muted-foreground" />
                    )}
                  </button>
                );
              })}
              {(!activeBoard?.columns?.length) && (
                <div className="text-xs text-muted-foreground italic">
                  This pipeline has no columns yet.
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}
                  className="rounded-full">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
